import { twMerge } from "tailwind-merge";

import { useGetUserType } from "@/modules/auth/hooks/userType.hooks";
import {
  IManagement,
  IManagementGroup,
} from "@/modules/profile/interfaces/management";
import history from "@/routes/history";

import { useGetAgencyTreeData } from "./agencyTreeData.hooks";
import {
  BanManagementModalProps,
  ManagementType,
  RemoveEntityModalProps,
  UserManagementModalProps,
} from "../types/commonTypes";

interface GetGroupTreeDataProps {
  management: IManagement;
  handleUserManagementModal?: (props: UserManagementModalProps) => void;
  handleBanManagementModal?: (props: BanManagementModalProps) => void;
  handleRemoveEntityModal?: (props: RemoveEntityModalProps) => void;
}

export const useGetGroupTreeData = () => {
  const { getAgencyTreeData } = useGetAgencyTreeData();

  const { canCreateAgency, canViewGroup, canUpdateGroup, canDeleteGroup } =
    useGetUserType();

  const getGroupTreeData = ({
    management,
    handleUserManagementModal,
    handleBanManagementModal,
    handleRemoveEntityModal,
  }: GetGroupTreeDataProps) => {
    if (!management?.groups) return [];

    return management.groups
      .filter(
        (
          group
        ): group is IManagementGroup & {
          id: string;
          label: string;
          agencies: Array<{ id: string; label: string }>;
        } => Boolean(group?.id && group?.label)
      )
      .map((group) => {
        const blockedText = group?.isDisabled
          ? "Débloquer ce groupe"
          : "Bloquer ce groupe";

        return {
          id: group.id,
          name: group.label,
          type: ManagementType.GROUP,
          isDisabled: group.isDisabled,
          reasonDisabled: group.reasonDisabled,
          actions: [
            ...(canCreateAgency
              ? [
                  {
                    text: (
                      <span>
                        Ajouter <span className="font-bold">une agence</span>{" "}
                        dans ce groupe
                      </span>
                    ),
                    icon: (
                      <i className="pi pi-plus text-[15px] text-extrabold" />
                    ),
                    onClick: () => {
                      history.push("/management/agency", {
                        parentId: group?.id,
                      });
                    },
                  },
                ]
              : []),
            ...(canUpdateGroup
              ? [
                  {
                    text: "Editer ce groupe",
                    icon: <i className="pi pi-pencil text-[14px]" />,
                    onClick: () => {
                      history.push(`/management/group/${group?.id}`);
                    },
                  },
                ]
              : []),
            ...(canViewGroup
              ? [
                  {
                    text: "Consulter ce groupe",
                    icon: <i className="pi pi-eye text-[16.6px]" />,
                    onClick: () => {
                      history.push(`/management/group/${group?.id}`, {
                        readOnly: true,
                      });
                    },
                  },
                ]
              : []),
            ...(canDeleteGroup
              ? [
                  {
                    text: blockedText,
                    icon: (
                      <i
                        className={twMerge(
                          "pi pi-ban text-[14.5px] text-red-FF0000 font-semibold",
                          group?.isDisabled && "pi pi-check-circle text-success"
                        )}
                      />
                    ),
                    onClick: () => {
                      handleBanManagementModal?.({
                        id: group?.id,
                        data: {
                          is_disabled: group?.isDisabled,
                          reason_disabled: group?.reasonDisabled,
                          level: group?.label,
                          type: ManagementType.GROUP,
                          location: `/management/group/${group?.id}`,
                          permissions: {
                            show: true,
                            update: true,
                            delete: true,
                          },
                        },
                      });
                    },
                  },
                  {
                    text: "Supprimer ce groupe",
                    icon: (
                      <i className="pi pi-trash text-[14px] text-red-FF0000 font-semibold" />
                    ),
                    onClick: () => {
                      handleRemoveEntityModal?.({
                        id: group?.id,
                        userType: ManagementType.GROUP,
                        actionType: "delete",
                        label: group?.label,
                      });
                    },
                  },
                ]
              : []),
          ],
          children: getAgencyTreeData({
            groupId: group.id,
            agencies: group.agencies,
            handleUserManagementModal,
            handleBanManagementModal,
            handleRemoveEntityModal,
          }),
        };
      });
  };

  return { getGroupTreeData };
};
